<script>
import {useRoute} from "vue-router";
import HeroBanner from "../../components/landing/HeroBanner.vue";
import LandingBuilderControlBar from "./LandingBuilderControlBar.vue";
import {useLandingPageStore} from "../../../stores/landingPage";
import InfoSection from "../../components/landing/InfoSection.vue";
import DonatableTargetsList from "../../components/landing/DonatableTargetsList.vue";
import PageFooter from "../../components/landing/PageFooter.vue";
import PageHeader from "../../components/landing/PageHeader.vue";
import LoadingSpinner from "../../components/loadingSpinner.vue";
import Icon from "../../components/Icon.vue";
import {useOrganizationStore} from "../../../stores/organizations";
import Popup from "../../components/popup.vue";
import TabNavigation from "../../components/TabNavigation.vue";
import CustomColorPicker from "../brand/colorPicker.vue";
import ColorCard from "../brand/colorCard.vue";
import BrandingTab from "./BrandingTab.vue";
import GiftBasketSideBarButton from "../../landing/GiftBasketSidebarButton.vue";
import BaseModal from "../../components/BaseModal.vue";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";
import PageArticle from "../../components/landing/pageArticle.vue";
import AddSectionModal from "../../components/landing/AddSectionModal.vue";
import VideoSection from "../../components/landing/VideoSection.vue";

export default {
  name: "LandingPageBuilder",
  components: {
    VideoSection,
    AddSectionModal,
    PageArticle,
    BaseModal,
    GiftBasketSideBarButton,
    BrandingTab,
    ColorCard, CustomColorPicker,
    TabNavigation,
    Popup,
    Icon,
    LoadingSpinner, PageHeader, PageFooter, DonatableTargetsList, InfoSection, LandingBuilderControlBar, HeroBanner
  },
  setup() {
    const route = useRoute();
    const landingStore = useLandingPageStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();

    return {
      route,
      landingStore,
      organizationStore,
      analyticEventStore
    };
  },
  data() {
    return {
      loading: true,
      popupMessage: "",
      activeTab: "tab-1",
      brandColors: {},
      ignoreDraftWatcher: false,
      unsavedModalVisible: false,
      pendingNavigation: null,
      activeSection: null,
      clientWidth: window.innerWidth,
      minWidth: 1000,
      previewMode: 'desktop',
      showAddComponentModal: false,
      initialPageData: {} // Stores JSON object to reset to if changes are discarded
    };
  },
  computed: {
    pageData() {
      // TODO builder only working on landing page right now, need to expanding this for multiple pages
      return this.landingStore.draftLayout?.pages?.find((page) => page.page_type === 'landing_page');
    },
    orderedComponents() {
      const components = this.pageData?.components.filter(c => !c.archived) ?? [];

      const header = components.find(c => c.component_type === 'header');
      const hero = components.find(c => c.component_type === 'hero_banner');
      const footer = components.find(c => c.component_type === 'footer');

      const body = components
        .filter(c =>
          !['header', 'hero_banner', 'footer'].includes(c.component_type)
        )
        .sort((a, b) => a.order - b.order);

      return [
        ...(header ? [header] : []),
        ...(hero ? [hero] : []),
        ...body,
        ...(footer ? [footer] : [])
      ];
    },
    organization() {
      return this.landingStore.organization;
    },
    brand() {
      return this.landingStore.localBrand;
    },
    // isSaving() {
    //   return this.landingStore.saving;
    // },
    // isPublishing() {
    //   return this.landingStore.publishing;
    // },
    contactButtonTextColor() {
      return this.landingStore.getContrastTextColor(this.brand.color_accent);
    },
    isScreenTooSmall() {
      return this.clientWidth < this.minWidth;
    },
  },
  watch: {
    loading() {
      this.landingStore.dirtyDraft = false;
    },
    // Updates primary & accent color variants, as well as text colours
    'landingStore.localBrand.color_primary'() {
      this.landingStore.updateColors();
    },
    'landingStore.localBrand.color_accent'() {
      this.landingStore.updateColors();
    },
  },
  mounted() {
    this.load();
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    load() {
      const routeOrgSlug = this.route.params.org;
      const loadedOrgSlug = this.landingStore.organization?.slug;
      const orgMatches = routeOrgSlug && loadedOrgSlug && routeOrgSlug === loadedOrgSlug;
      if (this.landingStore.layoutId && this.landingStore.draftLayout?.id && orgMatches) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.ignoreDraftWatcher = true;
      this.landingStore.donationTargets = []; //sets length to 0 which causes a re-load
      this.landingStore.loadCurrentDraft().then(() => {
        this.landingStore.dirtyDraft = false;
        this.ignoreDraftWatcher = false; // Re-enable watcher after initial load
        this.loading = false;
      });
    },

    componentValues(component) {
      return this.landingStore.getComponentValues(component);
    },

    save() {
      return this.landingStore.saveCurrentDraft()
        .then((res) => {
          this.showPopup("Draft Saved!");
          this.analyticEventStore.logEvent({
            action: `landing-builder-saved-draft`,
            traceable_type: "Website::Layout",
            traceable_id: this.landingStore.layoutId,
            metadata: {
              page_updates: this.landingStore.landingPage
            }
          });
          return true;
        });
    },

    publishDraft() {
      // Merge the current branding values into the organization's brand. WHY?
      // this.landingStore.organization.brand = {...this.brandStore.localBrand};

      this.ignoreDraftWatcher = true;
      this.landingStore.publishDraft().then(() => {

        // this.brandStore.setBrand(this.landingStore.organization.brand);
        this.landingStore.setDefaultTextColors();

        this.showPopup("Draft Published!");
        this.landingStore.dirtyDraft = false;
        this.ignoreDraftWatcher = false;

        this.analyticEventStore.logEvent({
          action: `landing-builder-published-draft`,
        });
      });
    },

    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();
    },
    switchTab(tabName) {
      this.analyticEventStore.logEvent({
        action: `landing-builder-switch-to-${tabName}-tab`,
        metadata: {
          from_tab: this.activeTab,
          to_tab: tabName
        }
      });
      this.activeTab = tabName;
      this.$refs.tabNavigation.switchTab(tabName);
    },
    resetBrand() {
      this.landingStore.resetBrand();

      if (this.brandDirty) {
        this.brandLoading = true;
        this.landingStore.resetBrand();
        setTimeout(() => {
          this.brandLoading = false;
          this.brandDirty = false;
        }, 100);
      }
      this.tempLogoFile = null;
      this.logoCleared = false;
    },

    showUnsavedModal(callback) {
      this.pendingNavigation = callback;
      this.unsavedModalVisible = true;
    },

    handleBuilderModalSave() {
      const saves = [];
      if (this.landingStore.dirtyDraft) {
        saves.push(this.save());
      }
      //Is a user doesn't save brand changes, this will do it if they leave the page
      if (this.landingStore.brandDirty) {
        saves.push(this.landingStore.saveGlobalSettings());
      }
      //Promise all takes everything in 'saves' array (draft and brand draft). It waits for all to finish before the .then
      Promise.all(saves).then(() => {
        if (this.pendingNavigation) {
          this.pendingNavigation.call();
          this.unsavedModalVisible = false;
          this.pendingNavigation = null;
        }
      });
    },

    handleBuilderModalDiscard() {
      this.landingStore.resetDraftChanges();

      if (this.pendingNavigation) {
        this.pendingNavigation?.call(); // run the callback (switching tab)
      }
    },
    closeUnsavedModal() {
      this.unsavedModalVisible = false;
      this.pendingNavigation = null;
    },
    closeAddComponentModal() {
      this.showAddComponentModal = false;
    },

    handleAccordionClick(section) {
      this.activeSection = section;
    },
    onWindowResize() {
      this.clientWidth = window.innerWidth;
    },
    setPreviewMode(mode) {
      this.previewMode = mode;
    },
  }
};
</script>

<template>
  <div
    v-if="loading"
    class="flex justify-center items-center h-screen"
  >
    <loading-spinner />
  </div>

  <div
    v-else
    class="page-container flex flex-row"
    :class="{ 'h-full': previewMode === 'mobile' || previewMode === 'tablet' }"
  >
    <div class="landing-page-container w-full flex flex-col h-full">
      <!-- old: :class="{ 'justify-between': previewMode==='desktop', 'justify-center': previewMode==='mobile' || previewMode === 'tablet' }" -->
      <div class="design-hub-container flex flex-row h-full w-full bg-white relative overflow-hidden justify-between">
        <!-- Overlay when too small -->
        <div
          v-if="!loading && isScreenTooSmall"
          class="overlay flex items-center justify-center"
        >
          <div class="overlay-content text-center">
            <p class="text-white text-xl mb-4">
              Your browser is too small!
            </p>
            <p class="text-white text-m">
              Resize your browser to be at least {{ minWidth }}px wide to use the Design Hub.
            </p>
          </div>
        </div>
        <!-- single “preview pane” that just swaps its classes -->
        <div
          id="landing-page-editor-container"
          class="h-full flex-1"
          :class="previewMode==='desktop'
            ? 'preview-desktop'
            : previewMode === 'tablet'
              ? 'preview-tablet'
              : 'preview-mobile'"
        >
          <!-- old: flex-1 was only on desktop
          :class="previewMode==='desktop'
                ? 'flex-1 preview-desktop'
                : previewMode === 'tablet'
                  ? 'preview-tablet'
                  : 'preview-mobile'"
          -->
          <div
            :class="previewMode === 'desktop' ? 'desktop-frame' : previewMode === 'tablet' ? 'tablet-frame' : 'phone-frame'"
          >
            <div
              :class="previewMode === 'desktop' ? 'desktop-screen' : previewMode === 'tablet' ? 'tablet-screen @container' : 'phone-screen @container'"
            >
              <div
                v-for="component in orderedComponents"
                :key="component.id"
              >
                <page-header
                  v-if="component.component_type === 'header'"
                  :org-name="organization?.name"
                  :logo-resource="brand.logo"
                  :show-logo="componentValues(component)?.logo"
                  :donate-button-background-color="brand.color_primary"
                  :donate-button-hover-background-color="brand.color_primary_dark"
                  disable-navigation
                  :class="{ 'active-section': activeSection === 'header' }"
                >
                  <template #right-icon-slot>
                    <gift-basket-side-bar-button disable-sidebar />
                  </template>
                </page-header>

                <hero-banner
                  v-if="component.component_type === 'hero_banner'"
                  v-bind="componentValues(component)"
                  :class="{ 'active-section': activeSection === 'hero_banner' }"
                />

                <video-section
                  v-if="component.component_type === 'video'"
                  :title="component.component_data.title?.value"
                  :subtitle="component.component_data.subtitle?.value"
                  :video="component.component_data.video?.value"
                  :autoplay="component.component_data.autoplay?.value"
                  :muted="component.component_data.muted?.value"
                />

                <page-article
                  v-if="component.component_type === 'article'"
                  v-bind="componentValues(component)"
                />
                <donatable-targets-list
                  v-if="component.component_type === 'donatable_grid'"
                  v-bind="componentValues(component)"
                  :donation-targets="landingStore.donationTargets"
                  :organization="organization"
                  :primary-color="brand.color_primary"
                  :primary-dark-color="brand.color_primary_dark"
                  :primary-text-color="landingStore.getContrastTextColor(brand.color_primary)"
                  disable-click
                />
                <page-footer
                  v-if="component.component_type === 'footer'"
                  v-bind="componentValues(component)"
                  :layout="componentValues(component)?.showFooter ? 'contact' : 'basic'"
                  :logo-src="componentValues(component)?.logo ? organization.logo_url : null"
                  :organization="organization"
                  :background-color="landingStore.getFooterBackgroundColor()"
                  :text-color="landingStore.getContrastTextColor(landingStore.getFooterBackgroundColor())"
                  :contact-button-background-color="brand.color_accent"
                  :contact-button-text-color="contactButtonTextColor"
                  disable-navigation
                  :class="{ 'active-section': activeSection === 'footer' }"
                />
              </div>
            </div>

            <!-- Floating Give Now Button Preview -->
            <button
              class="floating-give-btn"
              :style="{
                backgroundColor: brand?.color_primary,
                color: landingStore.getContrastTextColor(brand?.color_primary)
              }"
            >
              <icon
                icon="hand-heart"
                size="28px"
                :color="landingStore.getContrastTextColor(brand?.color_primary)"
              />
            </button>
          </div>
        </div>

        <!-- Control bar alongside -->
        <landing-builder-control-bar
          :preview-mode="previewMode"
          @update:preview-mode="setPreviewMode"
          @cancel="handleBuilderModalDiscard"
          @save="save"
          @publish="publishDraft"
          @accordion-click="handleAccordionClick"
          @show-add-component-modal="showAddComponentModal = true"
        />
      </div>
    </div>
  </div>

  <popup
    ref="popup"
    :message="popupMessage"
    :duration="1400"
  />
  <base-modal
    v-if="unsavedModalVisible"
    title="Unsaved Changes"
    :background-dismissible="false"
    @close="closeUnsavedModal"
  >
    <!--  Leaving Design Hub Draft change Confirmation-->
    <template #default>
      <p>You have unsaved changes on the Builder. Do you want to save or discard them?</p>
      <div class="mt-4 flex justify-end gap-2">
        <button
          class="btn-filled"
          @click="handleBuilderModalSave"
        >
          Save Changes
        </button>
        <button
          class="btn-outline"
          @click="handleBuilderModalDiscard"
        >
          Discard Changes
        </button>
      </div>
    </template>
  </base-modal>
  <base-modal
    v-if="showAddComponentModal"
    class="absolute"
    @close="closeAddComponentModal"
  >
    <add-section-modal
      @close="closeAddComponentModal"
    />
  </base-modal>
</template>

<style scoped lang="scss">
@import '@/styles/_variables.scss';


.landing-page-container {
  // setup container widths
  .outer-container {
    background-color: $color-section-bg;
    width: 100%;
    display: flex;
    justify-content: center;

    :deep(.inner-container) {
      width: 100%;
      max-width: $content-max-width;
      padding: $space-xl;
      margin: $space-md $space-lg;
    }
  }

  // tablet s
}

// Container queries for preview mode
// This needs to line up with the main media queries in the landing builder
// This uses the @container to query for size for mobile / tablet previews
@container (min-width: 0px) and (max-width: 639px) { // Breakpoint variables not working for some reason $breakpoint-min-sm
  .outer-container{
    :deep(.inner-container) {
      padding: $space-md $space-lg !important;
      margin: $space-md 0 !important;
    }
  }
}

@container (min-width: 640px) { // Breakpoint variables not working for some reason $breakpoint-min-sm
  .outer-container{
    :deep(.inner-container) {
      padding: $space-lg !important;
      margin: $space-md $space-lg !important;
    }
  }
}

.tab-label {
  position: relative;
  display: inline-block;
}


//
//.active-section {
//  // CSS for active selection, was using for debugging, will be handy in future. Might need tweaking
//}


/* Overlay only inside that box, not full window */
.design-hub-container > .overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 9999;

  /* Center the message box horizontally, push to the top */
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 20rem; /* distance from top */
  pointer-events: all;
}


.overlay-content {
  padding: 2rem;
  background: rgba(30, 30, 30, 0.9);
  border-radius: 0.5rem;
  max-width: 90%;
  text-align: center;
}

/* Desktop preview: full-width scroll */
#landing-page-editor-container.preview-desktop {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: #f3f4f6;
  overflow: hidden;
  box-sizing: border-box;
}

/* Desktop Bezel */
.desktop-frame {
  background-color: white;
  border: 10px solid black;
  border-radius: 20px;
  height: 90vh;
  width: calc(85vh * 1.5);
  max-width: 100%;
  min-width: 0;
  flex-shrink: 1;
  padding: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin: 1rem auto;
}

.desktop-screen {
  //padding: 10px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 100%;
  background: white;
  //border-radius: 16px;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}

/* hide scrollbars on desktop screen */
.desktop-screen::-webkit-scrollbar {
  width: 0;
  height: 0;
  background: transparent;
}

/* Mobile preview wrapper */
#landing-page-editor-container.preview-mobile {
  display: flex;
  justify-content: center;
  align-items: center;

  /* fill out the full preview pane */
  width: 100%;
  height: 100%;
  padding-top: 5rem;
  padding-bottom: 8rem;


  /* light gray behind the phone */
  background-color: #f3f4f6;


  /* prevent any outer scrollbars in this pane */
  overflow: hidden;
  box-sizing: border-box;
}

/* Tablet preview wrapper */
#landing-page-editor-container.preview-tablet {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  /* padding-top: 5rem; */
  /* padding-bottom: 8rem; */
  padding: 1rem;
  background-color: #f3f4f6;
  overflow: hidden;
  box-sizing: border-box;
}

/* Phone bezel */
.phone-frame {
  width: 375px;
  height: 767px;
  padding: 13px;
  background: #353535;
  border-radius: 40px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-top: 4rem;
}

/* Notch */
.phone-frame::before {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 20px;
  background: #353535;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.phone-screen {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 100%;
  padding-left: 1.5px;
  padding-right: 1.5px;
  background: white;
  border-radius: 24px;
  overflow-x: hidden;
  overflow-y: auto;

  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}

/* Optional: hide scrollbars on the phone screen */
.phone-screen::-webkit-scrollbar {
  width: 0;
  height: 0;
  background: transparent;
}

.phone-screen::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

/* Tablet bezel */
.tablet-frame {
  height: 85vh;
  width: calc(85vh * 0.75);
  min-width: 660px;
  padding: 8px;
  background: #353535;
  border-radius: 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin: 1rem auto;
}

.tablet-screen {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 100%;
  background: white;
  border-radius: 16px;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}

/* hide scrollbars on tablet screen */
.tablet-screen::-webkit-scrollbar {
  width: 0;
  height: 0;
  background: transparent;
}

.tablet-screen::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

/* Small viewport (e.g. 1366x768) */
@media (max-height: 768px) {
  .desktop-frame {
    height: 80vh;
    width: calc(75vh * 1.5);
    max-width: calc(75vh * 1.5);
  }

  .tablet-frame {
    height: 80vh;
    width: calc(80vh * 0.75);
    min-width: 660px;
  }

  .phone-frame {
    max-height: 90vh;
    width: 300px;
  }
}

.floating-give-btn {
  position: absolute;
  bottom: 24px;
  right: 24px;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: default;
  border: none;
  padding: 0;
}
</style>
